import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import TextType from './TextType';

export const MissionVision: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section className="py-12 md:py-20 bg-gradient-to-b from-[#FFF5F7] to-white relative overflow-hidden" ref={ref}>
      {/* Floating background decorations */}
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-32 h-32 rounded-full opacity-10"
          style={{
            background: i % 2 === 0 
              ? 'linear-gradient(135deg, #CA1167, #F47749)' 
              : 'linear-gradient(135deg, #F47749, #CA1167)',
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, 20, 0],
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360]
          }}
          transition={{
            duration: 8 + i * 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.5
          }}
        />
      ))}

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section - 2 Columns */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-start mb-6 md:mb-10"
          initial={{ opacity: 0, y: -30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          {/* Left Column - Title */}
          <div>
            <TextType
              baseOpacity={0}
              enableBlur={true}
              baseRotation={5}
              blurStrength={10}
              size="lg"
              duration={1}
              textClassName="font-bold text-[28px] md:text-[56px] leading-tight text-[#6B0D3E]"
            >
              Premium Packaging
            </TextType>
            <TextType
              baseOpacity={0}
              enableBlur={true}
              baseRotation={5}
              blurStrength={10}
              size="lg"
              duration={1}
              textClassName="font-bold text-[28px] md:text-[56px] leading-tight text-[#6B0D3E]"
            >
              We Craft.
            </TextType>
          </div>

          {/* Right Column - Trusted Badge */}
          <motion.div
            className="flex items-center justify-end gap-3"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0, rotate: -180 }}
                  animate={isInView ? { opacity: 1, scale: 1, rotate: 0 } : {}}
                  transition={{ 
                    delay: 0.6 + i * 0.1,
                    type: "spring",
                    stiffness: 200
                  }}
                  whileHover={{ 
                    scale: 1.3, 
                    rotate: 360,
                    transition: { duration: 0.3 }
                  }}
                  className="text-orange-400 text-xl cursor-pointer"
                >
                  ★
                </motion.span>
              ))}
            </div>
            <motion.span 
              className="text-gray-600 font-medium text-sm"
              animate={{ opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Trusted By Many People
            </motion.span>
            <div className="flex -space-x-2">
              {['/Images/s2_client.webp', '/Images/s2_client.webp', '/Images/s2_client.webp'].map((img, i) => (
                <motion.img
                  key={i}
                  src={img}
                  alt="Client"
                  className="w-10 h-10 rounded-full border-2 border-white object-cover"
                  initial={{ opacity: 0, x: -20, scale: 0 }}
                  animate={isInView ? { opacity: 1, x: 0, scale: 1 } : {}}
                  transition={{ 
                    delay: 0.8 + i * 0.1,
                    type: "spring",
                    stiffness: 150
                  }}
                  whileHover={{ 
                    scale: 1.2, 
                    zIndex: 10,
                    transition: { duration: 0.2 }
                  }}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Mission and Vision - 2x2 Grid, stacks on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {/* Top Left - Mission Image */}
          <motion.div
            className="relative group"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            whileHover={{ scale: 1.02 }}
            style={{ minHeight: '500px' }}
          >
            <div className="relative overflow-hidden rounded-3xl shadow-2xl bg-pink-100 h-full">
              <motion.img
                src="/Images/about_Our Mission.webp"
                alt="Our Mission"
                className="w-full h-full object-cover"
                whileHover={{ scale: 1.1, rotate: 2 }}
                transition={{ duration: 0.6 }}
              />
              
              {/* Animated corner accent */}
              <motion.div
                className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#F47749] to-transparent opacity-50"
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 90, 0]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />

              {/* Floating particles */}
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-3 h-3 bg-white rounded-full opacity-60"
                  style={{
                    top: `${20 + i * 30}%`,
                    left: `${10 + i * 20}%`,
                  }}
                  animate={{
                    y: [0, -20, 0],
                    opacity: [0.3, 0.8, 0.3],
                    scale: [1, 1.5, 1]
                  }}
                  transition={{
                    duration: 2 + i * 0.5,
                    repeat: Infinity,
                    delay: i * 0.3
                  }}
                />
              ))}
            </div>
          </motion.div>

          {/* Top Right - Mission Text */}
          <motion.div
            className="bg-white rounded-3xl shadow-lg p-8 flex flex-col justify-center relative overflow-hidden"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{ border: '1px solid #E7B1C5' }}
            whileHover={{ 
              boxShadow: '0 0 30px rgba(231, 177, 197, 0.5)',
              borderColor: '#CA1167'
            }}
          >
            {/* Animated gradient background */}
            <motion.div
              className="absolute inset-0 opacity-5"
              style={{
                background: 'linear-gradient(135deg, #CA1167, #F47749)'
              }}
              animate={{
                rotate: [0, 360]
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
            />

            <motion.p 
              style={{
                fontFamily: 'Instrument Sans',
                fontWeight: 900,
                fontStyle: 'italic',
                fontSize: '20px',
                lineHeight: '20px',
                letterSpacing: '2%',
                color: '#000000',
                marginBottom: '16px'
              }}
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.6 }}
            >
              OUR MISSION
            </motion.p>

            {/* <motion.h3 
              style={{
                fontFamily: 'Nanum Myeongjo',
                fontSize: '36.46px',
                lineHeight: '47.4px',
                letterSpacing: '0%',
                marginBottom: '20px'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.7 }}
            >
              <motion.span 
                style={{ fontWeight: 700 }}
                whileHover={{ color: '#CA1167' }}
              >
                Our technology and 
              </motion.span>
              <motion.span 
                style={{ fontWeight: 800 }}
                whileHover={{ scale: 1.05 }}
              >
                production power.
              </motion.span>
            </motion.h3> */}

            <motion.p 
              style={{
                fontFamily: 'Instrument Sans',
                fontWeight: 500,
                fontSize: '16.83px',
                lineHeight: '21.88px',
                letterSpacing: '0%',
                color: '#000000',
                marginBottom: '16px'
              }}
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.8 }}
            >
              Our mission is to provide superior mono carton packaging that meets the diverse needs of industries like pharmaceuticals, FMCG, cosmetics, and more. We are committed to:
            </motion.p>

            <ul className="space-y-3 relative z-10">
              {[
                { title: 'Quality & Value', desc: 'High-quality packaging at the best price.' },
                { title: 'Empowerment', desc: 'Job creation and skill development.' },
                { title: 'Sustainability', desc: 'Eco-friendly materials and processes.' },
                { title: 'Client Focus', desc: 'Trust, innovation, and lasting relationships.' }
              ].map((item, i) => (
                <motion.li
                  key={i}
                  className="flex items-start gap-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.9 + i * 0.1 }}
                  whileHover={{ x: 10 }}
                >
                  <div className="flex-shrink-0 w-6 h-6 mt-1">
                    <img src="/Images/check_icon.webp" alt="check" className="w-full h-full object-contain" />
                  </div>
                  <div className="flex flex-col">
                    <span style={{
                      fontFamily: 'Instrument Sans',
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#6B0D3E'
                    }}>
                      {item.title}
                    </span>
                    <span style={{
                      fontFamily: 'Instrument Sans',
                      fontSize: '14px',
                      fontWeight: 400,
                      color: '#4A4A4A'
                    }}>
                      {item.desc}
                    </span>
                  </div>
                </motion.li>
              ))}
            </ul>

            {/* <ul className="space-y-3 relative z-10">
              {[
                'High-Precision Printing',
                'Advanced Die-Cutting',
                'Sustainable Finishing',
                'FMCG & Food'
              ].map((item, i) => (
                <motion.li
                  key={i}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.9 + i * 0.1 }}
                  whileHover={{ x: 10, color: '#CA1167' }}
                  style={{
                    fontFamily: 'Instrument Sans',
                    fontSize: '14px',
                    color: '#000000'
                  }}
                >
                  <motion.span 
                    className="text-green-500 text-lg"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.3
                    }}
                  >
                    ✓
                  </motion.span>
                  {item}
                </motion.li>
              ))}
            </ul> */}
          </motion.div>

          {/* Bottom Left - Vision Text */}
          <motion.div
            className="bg-white rounded-3xl shadow-lg p-8 flex flex-col justify-center relative overflow-hidden"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
            style={{ border: '1px solid #E7B1C5' }}
            whileHover={{ 
              boxShadow: '0 0 30px rgba(231, 177, 197, 0.5)',
              borderColor: '#CA1167'
            }}
          >
            {/* Animated gradient background */}
            <motion.div
              className="absolute inset-0 opacity-5"
              style={{
                background: 'linear-gradient(135deg, #F47749, #CA1167)'
              }}
              animate={{
                rotate: [0, -360]
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
            />

            <motion.p 
              style={{
                fontFamily: 'Instrument Sans',
                fontWeight: 900,
                fontStyle: 'italic',
                fontSize: '20px',
                lineHeight: '20px',
                letterSpacing: '2%',
                color: '#000000',
                marginBottom: '16px'
              }}
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.7 }}
            >
              OUR VISION
            </motion.p>

            {/* <motion.h3 
              style={{
                fontFamily: 'Nanum Myeongjo',
                fontSize: '36.46px',
                lineHeight: '47.4px',
                letterSpacing: '0%',
                marginBottom: '20px'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8 }}
            >
              <motion.span 
                style={{ fontWeight: 700 }}
                whileHover={{ color: '#F47749' }}
              >
                Our technology and&nbsp;
              </motion.span>
              <motion.span 
                style={{ fontWeight: 800 }}
                whileHover={{ scale: 1.05 }}
              >
                production power.
              </motion.span>
            </motion.h3> */}

            <motion.p 
              style={{
                fontFamily: 'Instrument Sans',
                fontWeight: 500,
                fontSize: '16.83px',
                lineHeight: '21.88px',
                letterSpacing: '0%',
                color: '#000000',
                marginBottom: '24px'
              }}
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.9 }}
            >
              At <b>PrintAura Packaging</b>, we aspire to become a leading name in printing and packaging of mono cartons, known for affordable pricing, high-quality solutions, and social responsibility. Our goal is to expand our reach while creating job opportunities for those in need, helping them build a better future. By combining innovation, efficiency, and ethical business practices, we aim to serve businesses with cost-effective, reliable, and sustainable packaging solutions that add value beyond protection.
            </motion.p>

            {/* <ul className="space-y-3 relative z-10">
              {[
                'High-Precision Printing',
                'Advanced Die-Cutting',
                'Sustainable Finishing',
                'FMCG & Food'
              ].map((item, i) => (
                <motion.li
                  key={i}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 1.0 + i * 0.1 }}
                  whileHover={{ x: 10, color: '#F47749' }}
                  style={{
                    fontFamily: 'Instrument Sans',
                    fontSize: '14px',
                    color: '#000000'
                  }}
                >
                  <motion.span 
                    className="text-green-500 text-lg"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.3
                    }}
                  >
                    ✓
                  </motion.span>
                  {item}
                </motion.li>
              ))}
            </ul> */}
          </motion.div>

          {/* Bottom Right - Vision Image */}
          <motion.div
            className="relative group"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            whileHover={{ scale: 1.02 }}
            style={{ minHeight: '500px' }}
          >
            <div className="relative overflow-hidden rounded-3xl shadow-2xl bg-pink-100 h-full">
              <motion.img
                src="/Images/about_our vision.webp"
                alt="Our Vision"
                className="w-full h-full object-cover"
                whileHover={{ scale: 1.1, rotate: -2 }}
                transition={{ duration: 0.6 }}
              />
              
              {/* Animated corner accent */}
              <motion.div
                className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tl from-[#CA1167] to-transparent opacity-50"
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, -90, 0]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
              />

              {/* Floating particles */}
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-3 h-3 bg-white rounded-full opacity-60"
                  style={{
                    bottom: `${20 + i * 30}%`,
                    right: `${10 + i * 20}%`,
                  }}
                  animate={{
                    y: [0, 20, 0],
                    opacity: [0.3, 0.8, 0.3],
                    scale: [1, 1.5, 1]
                  }}
                  transition={{
                    duration: 2 + i * 0.5,
                    repeat: Infinity,
                    delay: i * 0.3
                  }}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

